import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EditPencilIcon, HotelSample, Smallarw } from '../../assests/ExportImageIcon'
import Modal from '../../componants/modal/Modal'
import service from '../../services/service'
import endpoints from '../../services/endpoints'
import { showToast } from '../../componants/toast/toastService'
import './venueModals.css'
import './venueBookingConfirm.css'

const BackArrow = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M15 9H4M8 5l-4 4 4 4" stroke="#026D4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const STATUS_LABELS = {
    pending: 'Awaiting Review',
    awaiting: 'Awaiting Review',
    negotiating: 'Negotiating',
    accepted: 'Accepted',
    rejected: 'Rejected',
}

const formatMoney = (value, currency) => {
    if (value === undefined || value === null || value === '') return '—'
    const amount = Number(value)
    if (Number.isNaN(amount)) return String(value)
    const symbol = !currency || currency === 'INR' ? '₹' : `${currency} `
    return `${symbol}${amount.toLocaleString('en-IN')}`
}

const formatDate = (dateStr) => {
    if (!dateStr) return ''
    // The API sometimes returns "YYYY-MM-DD HH:mm:ss" (space-separated, no
    // timezone) instead of strict ISO — normalize to "T" so this parses
    // reliably across browsers (Safari doesn't accept the space variant).
    const normalized = typeof dateStr === 'string' ? dateStr.replace(' ', 'T') : dateStr
    const date = new Date(normalized)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
}

const mapRfp = (rfp) => ({
    // Confirmed live 2026-09-21: this list's own `id` (1, 2, ...) is what
    // Edit RFP / Send to More Venues use — `proposal_id` (147, 148, ...) is
    // a separate id only needed later, when accepting a specific vendor's
    // proposal/quotation. Kept alongside as `proposalId` for
    // whenever this page grows an accept action.
    id: rfp.id,
    proposalId: rfp.proposal_id ?? null,
    image: rfp.venue_cover_photo || HotelSample,
    // The RFP is requested against one specific venue — that venue's name is
    // the card's headline (event type is the smaller sub-line), not the
    // other way round.
    venueName: rfp.venue_name || rfp.venue?.name || rfp.event_type_name || 'RFP',
    eventType: rfp.event_type_name || '',
    date: formatDate(rfp.start_time),
    guests: rfp.expected_guest_count ?? null,
    status: rfp.status || null,
})

const mapQuotation = (quotation) => {
    const status = String(quotation.status || 'pending').toLowerCase()
    return {
        // Confirmed live 2026-09-21: this row carries THREE different ids —
        // `id` (this list's own row id), `rfp_id` (the RFP), and
        // `quotation_id` (the actual quotation) — all different numbers.
        // "View proposal" needs `quotation_id` specifically, per Jatin.
        id: quotation.id,
        quotationId: quotation.quotation_id ?? null,
        name: quotation.venue_name || 'Vendor',
        status,
        statusLabel: quotation.status_badge || STATUS_LABELS[status] || 'Awaiting Review',
        basePrice: formatMoney(quotation.base_price, quotation.currency),
        totalPrice: formatMoney(quotation.total_price, quotation.currency),
        guests: quotation.expected_guest_count ?? '—',
        createdOn: formatDate(quotation.created_at),
        rfpId: quotation.rfp_id ?? null,
    }
}

const VenueBookingConfirm = () => {
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState('rfp')
    const [rfps, setRfps] = useState([])
    const [proposals, setProposals] = useState([])
    const [loadingRfps, setLoadingRfps] = useState(true)
    const [loadingProposals, setLoadingProposals] = useState(true)
    const [selectedIds, setSelectedIds] = useState([])
    const [editOpen, setEditOpen] = useState(false)
    const [editingRfpId, setEditingRfpId] = useState(null)
    const [editingProposalId, setEditingProposalId] = useState(null)
    const [editGuests, setEditGuests] = useState('')
    const [editMinBudget, setEditMinBudget] = useState('')
    const [editMaxBudget, setEditMaxBudget] = useState('')
    const [savingEdit, setSavingEdit] = useState(false)

    // Two independent lists on this page — every RFP the user has ever
    // submitted (not just the latest one), and every proposal any vendor has
    // sent back across all of them. Fetched independently so a failure on
    // one (e.g. quotations 400s when there are none yet) doesn't wipe out
    // the other's real data.
    useEffect(() => {
        setLoadingRfps(true)
        service.get(endpoints.venues.rfps, {}, { silent: true })
            .then(({ data }) => {
                const body = data?.data ?? data
                const list = Array.isArray(body) ? body : Array.isArray(body?.results) ? body.results : []
                // No created_at on this endpoint — ids are assigned in
                // creation order, so highest id first = most recent.
                const sorted = [...list].sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
                setRfps(sorted.map(mapRfp))
            })
            .catch(() => setRfps([]))
            .finally(() => setLoadingRfps(false))

        setLoadingProposals(true)
        service.get(endpoints.venues.rfpQuotations, {}, { silent: true })
            .then(({ data }) => {
                const body = data?.data ?? data
                const list = Array.isArray(body) ? body : Array.isArray(body?.results) ? body.results : []
                setProposals(list.map(mapQuotation))
            })
            .catch(() => setProposals([]))
            .finally(() => setLoadingProposals(false))
    }, [])

    const toggleSelect = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]))
    }

    const [negotiatingId, setNegotiatingId] = useState(null)

    // The quotations list (rfps/quotations) doesn't include chat_room_id —
    // only the proposal-detail response does. Negotiation is keyed by
    // chat_room_id now (confirmed 2026-09-21), so fetch the detail first to
    // get it, same id (quotationId) the URL itself uses for proposalAction/
    // confirm-booking downstream.
    const handleNegotiateClick = (proposal) => {
        if (!proposal.quotationId || negotiatingId) return
        setNegotiatingId(proposal.id)
        service
            .get(endpoints.venues.proposalDetail.replace('{proposal_id}', proposal.quotationId), {}, { silent: true })
            .then(({ data }) => {
                const body = data?.data ?? data ?? {}
                navigate(`/venue-negotiate/${proposal.quotationId}`, { state: { chatRoomId: body.chat_room_id ?? null } })
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setNegotiatingId(null))
    }

    const selectedProposals = proposals.filter((proposal) => selectedIds.includes(proposal.id))

    const handleCompare = () => {
        navigate('/venue-compare', { state: { proposals: selectedProposals } })
    }

    const openEdit = (item) => {
        // The rfps list endpoint doesn't return the budget range, so those
        // fields open blank — the user re-enters them to update.
        // Edit (PATCH .../proposals/{id}) needs `proposal_id` (147, 148 —
        // the id request-proposal returns on creation), NOT this list's own
        // `id` (1, 2) — per Jatin, that smaller id is only for Send to More
        // Venues (suggested-venues), which 404s/returns nothing on the
        // larger proposal_id instead.
        setEditingRfpId(item.id)
        setEditingProposalId(item.proposalId)
        setEditGuests(String(item.guests ?? ''))
        setEditMinBudget('')
        setEditMaxBudget('')
        setEditOpen(true)
    }

    const saveEdit = () => {
        if (!editingProposalId) {
            showToast('This RFP can no longer be edited', 'error')
            return
        }
        setSavingEdit(true)
        service
            .patch(endpoints.venues.proposalUpdate.replace('{proposal_id}', editingProposalId), {
                expected_guest_count: editGuests ? Number(editGuests) : undefined,
                budget_min: editMinBudget || undefined,
                budget_max: editMaxBudget || undefined,
                currency: 'INR',
            })
            .then(() => {
                setRfps((prev) => prev.map((item) => (
                    item.id === editingRfpId ? { ...item, guests: Number(editGuests) || item.guests } : item
                )))
                setEditOpen(false)
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setSavingEdit(false))
    }

    return (
        <>
            <section className='bookingConfirm commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <div className='row g-4'>
                        <div className='col-md-12'>
                            <div className='tripDocHeader' onClick={() => navigate(-1)}>
                                <BackArrow />
                                <span className='bsSectionTitle'>My RFPs</span>
                            </div>
                        </div>

                        <div className='col-md-12'>
                            <div className='venueTabToggle'>
                                <button
                                    type='button'
                                    className={`venueTabToggle__btn ${activeTab === 'rfp' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('rfp')}
                                >
                                    User RFP
                                </button>
                                <button
                                    type='button'
                                    className={`venueTabToggle__btn ${activeTab === 'proposals' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('proposals')}
                                >
                                    Proposal received
                                </button>
                            </div>
                        </div>

                        {activeTab === 'rfp' ? (
                            <div className='col-md-12'>
                                <div className='rfpLabel'>Your RFP</div>
                                {loadingRfps ? (
                                    <p className='hotelResultsEmpty'>Loading your RFPs...</p>
                                ) : rfps.length === 0 ? (
                                    <p className='hotelResultsEmpty'>No RFPs submitted yet.</p>
                                ) : (
                                    <div className='venueProposalList'>
                                        {rfps.map((item) => (
                                            <div className='bsBox rfpSummaryCardWrap' key={item.id}>
                                                <div className='rfpSummaryCard'>
                                                    <img src={item.image} alt={item.venueName} className='rfpSummaryCard__img' />
                                                    <div className='rfpSummaryCard__info'>
                                                        <div className='hotelCard__name'>{item.venueName}</div>
                                                        {item.eventType && <div className='hotelCard__locationNote'>{item.eventType}</div>}
                                                        {item.date && <div className='hotelCard__locationNote'>{item.date}</div>}
                                                        {item.guests != null && (
                                                            <div className='hotelCard__locationNote'>{item.guests} Guests</div>
                                                        )}
                                                    </div>
                                                    <button type='button' className='rfpSummaryCard__edit' aria-label="Edit request" onClick={() => openEdit(item)}>
                                                        <img src={EditPencilIcon} alt="" />
                                                    </button>
                                                </div>
                                                <button
                                                    type='button'
                                                    className='travellerAddBtn w-100 mt-3'
                                                    onClick={() => navigate(`/venue-suggested-venues/${item.id}`, { state: { originalProposalId: item.proposalId } })}
                                                >
                                                    Send to More Venues
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className='col-md-12'>
                                    <div className='bsSectionTitle'>
                                        {loadingProposals ? 'Loading proposals...' : `${proposals.length} proposal${proposals.length === 1 ? '' : 's'} received`}
                                    </div>
                                </div>

                                <div className='col-md-12'>
                                    {!loadingProposals && proposals.length === 0 ? (
                                        <p className='hotelResultsEmpty'>No proposals yet — vendors typically respond within 24-48 hours.</p>
                                    ) : (
                                        <div className='venueProposalList'>
                                            {proposals.map((proposal) => (
                                                <div
                                                    className={`venueProposalCard bgWhite commonBorder borderRadius20 ${selectedIds.includes(proposal.id) ? 'venueProposalCard--selected' : ''}`}
                                                    key={proposal.id}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => toggleSelect(proposal.id)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault()
                                                            toggleSelect(proposal.id)
                                                        }
                                                    }}
                                                >
                                                    <div className='venueProposalCard__body'>
                                                        <div className='venueProposalCard__head'>
                                                            <div className='hotelCard__name'>{proposal.name}</div>
                                                            <span className={`tripStatusBadge ${proposal.status === 'negotiating' ? 'is-negotiating' : 'is-pending'}`}>
                                                                {proposal.statusLabel}
                                                            </span>
                                                        </div>

                                                        <div className='bsFareRow mt-2'>
                                                            <span>Base Price:</span>
                                                            <span>{proposal.basePrice}</span>
                                                        </div>
                                                        <div className='bsFareRow'>
                                                            <span>Total Price</span>
                                                            <span>{proposal.totalPrice}</span>
                                                        </div>

                                                        <div className='hotelCard__locationNote mb-3'>
                                                            Upto {proposal.guests} Guests {proposal.createdOn ? <>&bull; {proposal.createdOn}</> : null}
                                                        </div>

                                                        <div className='venueProposalCard__actions'>
                                                            <button
                                                                type='button'
                                                                className='travellerAddBtn venueProposalCard__actionBtn'
                                                                disabled={negotiatingId === proposal.id}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    handleNegotiateClick(proposal)
                                                                }}
                                                            >
                                                                {negotiatingId === proposal.id ? 'Opening...' : proposal.status === 'negotiating' ? 'Continue Chat' : 'Negotiate'}
                                                            </button>
                                                            <button
                                                                type='button'
                                                                className='hotelInfoCard__arrow'
                                                                aria-label="View proposal"
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    // Corrected 2026-09-21 per Jatin, live-tested: this row
                                                                    // has THREE different ids (id / rfp_id / quotation_id) —
                                                                    // viewing a proposal needs `quotation_id` specifically,
                                                                    // not this list's own `id` and not the RFP id.
                                                                    navigate(`/venue-proposal-detail/${proposal.quotationId}`)
                                                                }}
                                                            >
                                                                <img src={Smallarw} alt="" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className='col-md-12'>
                                    <button
                                        type='button'
                                        className='commonBtn w-100 venueCompareBtn'
                                        disabled={selectedProposals.length < 2}
                                        onClick={handleCompare}
                                    >
                                        Compare{selectedProposals.length ? ` (${selectedProposals.length})` : ''}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </section>

            <Modal open={editOpen} onClose={() => setEditOpen(false)}>
                <div className='venueModal'>
                    <div className='venueModal__title'>Edit Your RFP</div>
                    <p className='venueModal__subtitle'>You can update your guest count and budget range.</p>

                    <div className='travellerFieldRow'>
                        <div className='travellerField'>
                            <label>Guest Count</label>
                            <input type='number' min={1} value={editGuests} onChange={(e) => setEditGuests(e.target.value)} />
                        </div>
                    </div>
                    <div className='travellerFieldRow'>
                        <div className='travellerField'>
                            <label>Min Budget</label>
                            <input type='number' min={0} value={editMinBudget} onChange={(e) => setEditMinBudget(e.target.value)} />
                        </div>
                        <div className='travellerField'>
                            <label>Max Budget</label>
                            <input type='number' min={0} value={editMaxBudget} onChange={(e) => setEditMaxBudget(e.target.value)} />
                        </div>
                    </div>

                    <div className='venueModal__actions mt-3'>
                        <button type='button' className='venueModalBtn' onClick={() => setEditOpen(false)}>Cancel</button>
                        <button type='button' className='commonBtn' disabled={savingEdit} onClick={saveEdit}>
                            {savingEdit ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    )
}

export default VenueBookingConfirm
